import { memo, useCallback } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Account } from "@common/types/domains/accounting/account";
import { AccountCombobox } from "@components/accounts/AccountCombobox";
import { useAccounts } from "@hooks/useAccounts";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useUpdateCatalogItem } from "@api/catalogItem.api";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { CatalogItemRow } from "../catalogItems.types";

// Item list's "Income account" column — edits the item's revenueAccountId,
// the default account propagated onto an invoice line's coding when this
// item is selected (see LineItemsSection.handleSelectItem).
export const AccrualAccountCell = memo(function AccrualAccountCell({
    row,
}: CellContext<ViewTableFeatures, CatalogItemRow, unknown>) {
    const item = row.original;
    const updateCatalogItem = useUpdateCatalogItem();
    const showAccountNumbers = useShowAccountNumbers();
    const { accounts } = useAccounts({ entityId: item.entityId });

    const account = accounts.find((a) => a.id === item.revenueAccountId);

    const handleChange = useCallback(
        async (selectedAccount: Account | null) => {
            await updateCatalogItem.mutateAsync({
                catalogItemId: item.id,
                body: { revenueAccountId: selectedAccount?.id ?? null },
            });
        },
        [updateCatalogItem, item.id],
    );

    if (item.isNew) {
        return <ViewTableCell />;
    }

    return (
        <ViewTableCell captureClicks>
            <AccountCombobox
                showAccountCode={showAccountNumbers}
                selectedAccount={account}
                onChange={handleChange}
                entityId={item.entityId}
                canClear
            />
        </ViewTableCell>
    );
});
